import { SelectProps, TextFieldProps } from "@mui/material";
import React from "react";
import CustomTextField from "../custom-text-field/CustomTextField";
import { icon, IconComponent } from "../../icon-component";
import styles from "./CustomSelect.module.css";

type CustomSelectSize = "sm" | "md";

type IconSize = React.ComponentProps<typeof IconComponent>["size"];

const chevronIconSizes: Record<CustomSelectSize, IconSize> = {
	sm: "2xs",
	md: "xs",
};

const createSelectChevron = (size: CustomSelectSize): React.FC<{ className?: string }> => {
	const SelectChevron: React.FC<{ className?: string }> = ({ className }) => (
		<IconComponent
			className={className}
			color="var(--icon-secondary)"
			icon={icon.faChevronDown}
			size={chevronIconSizes[size]}
		/>
	);
	return SelectChevron;
};

const isEmptyValue = (value: unknown): boolean =>
	value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0);

interface CustomSelectProps extends Omit<SelectProps, "size" | "variant" | "input"> {
	InputProps?: TextFieldProps["InputProps"];
	size?: CustomSelectSize;
}

const CustomSelect = React.forwardRef<HTMLDivElement, CustomSelectProps>(
	(
		{
			children,
			className = "",
			disabled = false,
			displayEmpty = false,
			error = false,
			fullWidth = false,
			InputProps = undefined,
			MenuProps = undefined,
			multiple = false,
			name = undefined,
			onChange,
			required = false,
			SelectDisplayProps = undefined,
			size = "md",
			value,
			...rest
		},
		ref
	) => (
		<CustomTextField
			ref={ref}
			select
			className={`${styles.select} ${className}`}
			data-disabled={disabled}
			data-filled={!isEmptyValue(value)}
			disabled={disabled}
			error={error}
			fullWidth={fullWidth}
			InputProps={{
				notched: false,
				...InputProps,
				classes: {
					notchedOutline: disabled ? styles.notchedOutlineDisabled : undefined,
					...InputProps?.classes,
				},
			}}
			name={name}
			required={required}
			SelectProps={{
				displayEmpty,
				IconComponent: createSelectChevron(size),
				multiple,
				...rest,
				MenuProps: {
					...MenuProps,
					classes: {
						...MenuProps?.classes,
						list: `${size === "sm" ? styles.menuListSm : styles.menuListMd} ${
							MenuProps?.classes?.list || ""
						}`,
					},
				},
				SelectDisplayProps: {
					...SelectDisplayProps,
					className: `${size === "sm" ? styles.triggerSm : styles.triggerMd} ${
						SelectDisplayProps?.className || ""
					}`,
				},
			}}
			size={size}
			value={value}
			variant="outlined"
			onChange={onChange as unknown as React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>}
		>
			{children}
		</CustomTextField>
	)
);

export default CustomSelect;
